// =====================================================================
// WorkspaceDashboard — Sprint 3 Phase 3H (2026-05-31)
// =====================================================================
// Top-of-page widget on the workspace landing route. Three sections:
//   - Severity totals strip (critical / warning / info)
//   - Per-project mini-cards with open-alert counts (link to project)
//   - Recent triggers ticker
// Refreshes every 30s via React Query so an operator can leave it
// open as an awareness display.
// =====================================================================

import type { ParsedAlertEvent, ParsedAlertSeverity } from '@rhia/protocol';
import { StatusDot } from '@rhia/ui';
import type * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { HelpHint } from '@/features/help/HelpHint';
import { useProjects } from '@/features/projects/api';
import { type ParsedProjectAlertCounts, useWorkspaceAlertSummary } from './workspaceSummaryApi';

export interface WorkspaceDashboardProps {
  workspaceId: string;
  workspaceSlug: string;
}

const SEV_LABEL_KEY: Record<ParsedAlertSeverity, string> = {
  critical: 'workspace.dashboard.sevCritical',
  warning: 'workspace.dashboard.sevWarning',
  info: 'workspace.dashboard.sevInfo',
};

const SEV_VALUE_CLASS: Record<ParsedAlertSeverity, string> = {
  critical: 'text-[color:var(--color-danger)]',
  warning: 'text-[color:var(--color-warning)]',
  info: 'text-[color:var(--color-info)]',
};

export function WorkspaceDashboard({
  workspaceId,
  workspaceSlug,
}: WorkspaceDashboardProps): React.ReactElement {
  const { t } = useTranslation();
  const summaryQ = useWorkspaceAlertSummary(workspaceId);
  const projectsQ = useProjects(workspaceId);

  const summary = summaryQ.data;
  const projectsById = new Map((projectsQ.data ?? []).map((p) => [p.id, p]));

  return (
    <Card data-testid="workspace-dashboard">
      <CardHeader className="flex flex-row items-baseline justify-between">
        <CardTitle className="flex items-center gap-1">
          {t('workspace.dashboard.title')}
          <HelpHint helpKey="dashboard.severity_tiles" />
        </CardTitle>
        {summaryQ.isFetching ? (
          <span className="text-xs text-[color:var(--color-muted-fg)]">
            {t('workspace.dashboard.refreshing')}
          </span>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-4">
        {summaryQ.error ? (
          <p className="text-sm text-[color:var(--color-danger)]">{summaryQ.error.message}</p>
        ) : null}

        {summary ? (
          <div className="grid grid-cols-3 gap-3" data-testid="severity-strip">
            {(['critical', 'warning', 'info'] as ParsedAlertSeverity[]).map((sev) => (
              <Card key={sev} data-testid={`sev-tile-${sev}`}>
                <CardContent className="py-3">
                  <div className="text-[10px] uppercase text-[color:var(--color-muted-fg)]">
                    {t(SEV_LABEL_KEY[sev])}
                  </div>
                  <div className={`text-2xl font-semibold ${SEV_VALUE_CLASS[sev]}`}>
                    {summary.totals.by_severity[sev]}
                  </div>
                  <div className="text-[10px] text-[color:var(--color-muted-fg)]">
                    {t('workspace.dashboard.openEvents')}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : null}

        {summary && summary.per_project.length > 0 ? (
          <div className="space-y-1">
            <h3 className="flex items-center gap-1 text-xs font-semibold uppercase">
              {t('workspace.dashboard.byProject')}
              <HelpHint helpKey="dashboard.per_project" />
            </h3>
            <ul className="space-y-1" data-testid="per-project-list">
              {summary.per_project
                .filter(
                  (p) => p.unacked + p.acked + p.assigned > 0 || projectsById.has(p.project_id),
                )
                .map((p) => (
                  <ProjectRow
                    key={p.project_id}
                    counts={p}
                    workspaceSlug={workspaceSlug}
                    projectName={projectsById.get(p.project_id)?.name}
                  />
                ))}
            </ul>
          </div>
        ) : null}

        {summary && summary.recent.length > 0 ? (
          <div className="space-y-1">
            <h3 className="flex items-center gap-1 text-xs font-semibold uppercase">
              {t('workspace.dashboard.recentTriggers')}
              <HelpHint helpKey="dashboard.recent_triggers" />
            </h3>
            <ul className="space-y-1" data-testid="recent-list">
              {summary.recent.slice(0, 5).map((evt) => (
                <RecentRow
                  key={evt.id}
                  event={evt}
                  workspaceSlug={workspaceSlug}
                  projectName={projectsById.get(evt.project_id)?.name}
                />
              ))}
            </ul>
          </div>
        ) : null}

        {summary &&
        summary.totals.unacked + summary.totals.acked + summary.totals.assigned === 0 ? (
          <p
            className="flex items-center gap-1.5 text-xs text-[color:var(--color-muted-fg)]"
            data-testid="all-clear"
          >
            <StatusDot status="ok" />
            {t('workspace.dashboard.allClear')}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

interface ProjectRowProps {
  counts: ParsedProjectAlertCounts;
  workspaceSlug: string;
  projectName: string | undefined;
}

function ProjectRow({ counts, workspaceSlug, projectName }: ProjectRowProps): React.ReactElement {
  const { t } = useTranslation();
  const total = counts.unacked + counts.acked + counts.assigned;
  return (
    <li
      className="flex flex-wrap items-center gap-2 text-sm"
      data-testid={`project-row-${counts.project_id}`}
    >
      <Link
        to={`/w/${workspaceSlug}/projects/${counts.project_id}/alerts`}
        className="font-semibold underline-offset-2 hover:underline"
      >
        {projectName ?? counts.project_id}
      </Link>
      <span className="text-[10px] text-[color:var(--color-muted-fg)]">
        {t('workspace.dashboard.projectOpen', { total, unacked: counts.unacked })}
      </span>
      {counts.by_severity.critical > 0 ? (
        <Badge variant="outline" className="text-[color:var(--color-danger)]">
          {t('workspace.dashboard.critical', { count: counts.by_severity.critical })}
        </Badge>
      ) : null}
      {counts.by_severity.warning > 0 ? (
        <Badge variant="outline" className="text-[color:var(--color-warning)]">
          {t('workspace.dashboard.warning', { count: counts.by_severity.warning })}
        </Badge>
      ) : null}
    </li>
  );
}

interface RecentRowProps {
  event: ParsedAlertEvent;
  workspaceSlug: string;
  projectName: string | undefined;
}

function RecentRow({ event, workspaceSlug, projectName }: RecentRowProps): React.ReactElement {
  return (
    <li
      className="flex flex-wrap items-baseline gap-2 text-xs"
      data-testid={`recent-row-${event.id}`}
    >
      <span className="font-mono text-[color:var(--color-muted-fg)]">
        {new Date(event.triggered_at_ms).toLocaleTimeString()}
      </span>
      <Badge variant="outline" className="uppercase">
        {event.rule_snapshot.severity}
      </Badge>
      <span className="font-medium">{event.rule_snapshot.name}</span>
      <Link
        to={`/w/${workspaceSlug}/projects/${event.project_id}/alerts`}
        className="text-[color:var(--color-muted-fg)] underline-offset-2 hover:underline"
      >
        {projectName ?? event.project_id}
      </Link>
      <span className="text-[color:var(--color-muted-fg)]">
        {event.triggered_by_tag_id} = {event.triggered_value}
      </span>
    </li>
  );
}
